import { audio } from '../audio/AudioManager.js';
import { animate, DURATION, EASING, isReducedMotion } from './anim.js';
import { clear, el } from './dom.js';

/** Messages ephemeres, empiles en haut de l'ecran, sous la zone sure. */

export type ToastKind = 'info' | 'success' | 'error';

let container: HTMLElement | null = null;

function host(): HTMLElement {
  if (container?.isConnected) return container;
  container = el('div', {
    class: 'toast-stack',
    attrs: { role: 'status', 'aria-live': 'polite' },
  });
  document.body.appendChild(container);
  return container;
}

export interface ToastOptions {
  readonly kind?: ToastKind;
  readonly duration?: number;
  /** `false` pour les messages purement visuels enchainés a un autre son. */
  readonly sound?: boolean;
}

export function toast(message: string, options: ToastOptions = {}): void {
  const kind = options.kind ?? 'info';
  const stack = host();

  const node = el('div', { class: 'toast', data: { kind } }, [
    el('span', { class: 'toast__bar' }),
    el('p', { class: 'toast__text', text: message }),
  ]);

  stack.appendChild(node);
  animate(
    node,
    [
      { opacity: 0, transform: 'translateY(-10px) scale(0.98)' },
      { opacity: 1, transform: 'none' },
    ],
    { duration: DURATION.base, easing: EASING.out },
  );

  if (options.sound !== false) audio.play(kind === 'error' ? 'error' : 'click');

  const life = options.duration ?? (kind === 'error' ? 4600 : 3200);
  window.setTimeout(() => {
    const leaving = animate(
      node,
      [
        { opacity: 1, transform: 'none' },
        { opacity: 0, transform: 'translateY(-8px)' },
      ],
      { duration: DURATION.micro, easing: EASING.in },
    );
    if (!leaving || isReducedMotion()) {
      node.remove();
      return;
    }
    void leaving.finished.then(() => node.remove()).catch(() => node.remove());
  }, life);
}

export function clearToasts(): void {
  if (container) clear(container);
}
